import { useState } from "react";

const fadeIn = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes pulse-soft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}
.anim-1 { animation: fadeUp 0.7s ease both; }
.anim-2 { animation: fadeUp 0.7s 0.12s ease both; }
.anim-3 { animation: fadeUp 0.7s 0.24s ease both; }
.anim-4 { animation: fadeUp 0.7s 0.36s ease both; }
.anim-fade { animation: fadeIn 1.2s ease both; }
.display-font { font-family: 'Playfair Display', Georgia, serif; }
.body-font { font-family: 'DM Sans', sans-serif; }
.grain {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 128px;
  pointer-events: none;
}
`;

const faqs = [
    {
        q: "How much time does this actually take each week?",
        a: "The 2027 Startup Sprint runs 5–10 hours per week. The Founder Track is 8–12 hours per week. Both are designed to fit around school, sports, and other commitments, but expect your student to be genuinely engaged, not just coasting."
    },
    {
        q: "Is my child working alone or with other students?",
        a: "Always in a team. We keep cohorts small and intentional. Each team owns specific roles (product, engineering, design, marketing) just like a real startup. The peer dynamic is a core part of what makes this work."
    },
    {
        q: "Who is actually supervising them?",
        a: "Every team is guided by a vetted Startup Trainer, a founder who has raised over $1M in funding and scaled a real product to 100,000+ users. They run three standups per week and are reachable on Slack throughout the program."
    },
    {
        q: "What if my child has no coding or business experience?",
        a: "Most of our students don't. That's by design. Students pick a role that fits their strengths. Engineering students learn by doing. Non-technical students lead product, design, and marketing. No prerequisites, only willingness to build."
    },
    {
        q: "What happens after the program ends?",
        a: "Their product stays live for at least one year. They receive a certificate of completion and a letter of recommendation from their Startup Trainer, a real founder, not a teacher. They also join the Extern Academy alumni network for ongoing opportunities."
    }
];

const outcomes = [
    { icon: "🚀", title: "A live product", desc: "Something real, launched to the world. Not a class project collecting dust." },
    { icon: "✉️", title: "Letter of recommendation", desc: "Written by a founder who raised capital and scaled a product. Not a teacher. A builder." },
    { icon: "🌐", title: "First professional network", desc: "Teammates, a trainer, and an alumni community they'll return to for years." },
    { icon: "🧠", title: "The builder mindset", desc: "They'll stop asking \"can I?\" and start asking \"how fast can we?\"" },
];

const comparisons = [
    { label: "vs. SAT Tutoring", note: "$150/hr for test prep", ea: "$850 for a shipped product and a founder mentor" },
    { label: "vs. College Counselor", note: "$300/session for essay coaching", ea: "$850 for two weeks of real execution they can write about" },
    { label: "vs. Summer Camp", note: "Lectures, simulations, certificates", ea: "Live product. Real users. Real stakes." },
];

function FAQ({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div
            className={`border-b border-stone-200 transition-all duration-300`}
            style={{ borderColor: "rgba(168,150,128,0.25)" }}
        >
            <button
                className="w-full text-left py-6 flex justify-between items-start gap-4 group"
                onClick={() => setOpen(!open)}
            >
                <span className="body-font font-500 text-stone-800 text-lg leading-snug" style={{ fontWeight: 500 }}>{q}</span>
                <span
                    className="text-2xl leading-none mt-0.5 transition-transform duration-300 flex-shrink-0"
                    style={{ color: "#b07d4a", transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
                >+</span>
            </button>
            {open && (
                <p className="body-font text-stone-500 pb-6 leading-relaxed text-base" style={{ fontWeight: 300 }}>{a}</p>
            )}
        </div>
    );
}

export default function MainStudentPageComponent() {
    return (
        <div className="body-font min-h-screen" style={{ background: "#faf8f5", color: "#2a2118" }}>
            <style>{fadeIn}</style>

            {/* NAV */}
            <nav className="anim-fade fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-5"
                 style={{ background: "rgba(250,248,245,0.92)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(168,150,128,0.15)" }}>
        <span className="display-font text-xl font-bold tracking-tight" style={{ color: "#2a2118" }}>
          Extern <span style={{ color: "#b07d4a" }}>Academy</span>
        </span>
                <a
                    href="#apply"
                    className="body-font text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-200"
                    style={{ background: "#b07d4a", color: "#fff", fontWeight: 600, letterSpacing: "0.02em" }}
                    onMouseEnter={e => e.target.style.background = "#96683b"}
                    onMouseLeave={e => e.target.style.background = "#b07d4a"}
                >
                    Apply Free →
                </a>
            </nav>

            {/* HERO */}
            <section className="relative pt-40 pb-32 px-6 overflow-hidden" style={{ background: "#1e1710" }}>
                <div className="grain absolute inset-0 z-0" />
                {/* decorative circle */}
                <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10"
                     style={{ background: "radial-gradient(circle, #b07d4a 0%, transparent 70%)" }} />
                <div className="relative z-10 max-w-4xl mx-auto text-center">
                    <div className="anim-1 inline-block mb-6 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase"
                         style={{ background: "rgba(176,125,74,0.15)", color: "#c9956a", border: "1px solid rgba(176,125,74,0.3)", letterSpacing: "0.12em" }}>
                        For Parents
                    </div>
                    <h1 className="anim-2 display-font text-5xl md:text-7xl font-black leading-none mb-8"
                        style={{ color: "#faf8f5", letterSpacing: "-0.02em" }}>
                        Your kid has<br />
                        <span style={{ color: "#b07d4a", fontStyle: "italic" }}>the grades.</span><br />
                        So does everyone else.
                    </h1>
                    <p className="anim-3 body-font text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10"
                       style={{ color: "rgba(250,248,245,0.6)", fontWeight: 300 }}>
                        Top colleges and competitive employers don't just want transcripts anymore. They want proof: that your student can build something, lead someone, and ship something real. Extern Academy gives them exactly that.
                    </p>
                    <div className="anim-4 flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="#apply"
                           className="px-8 py-4 rounded-full font-semibold text-base transition-all duration-200"
                           style={{ background: "#b07d4a", color: "#fff" }}
                           onMouseEnter={e => e.target.style.background = "#96683b"}
                           onMouseLeave={e => e.target.style.background = "#b07d4a"}
                        >
                            Apply Free. Takes 30 min →
                        </a>
                        <a href="#how-it-works"
                           className="px-8 py-4 rounded-full font-semibold text-base transition-all duration-200"
                           style={{ background: "rgba(250,248,245,0.08)", color: "rgba(250,248,245,0.8)", border: "1px solid rgba(250,248,245,0.15)" }}
                        >
                            See How It Works
                        </a>
                    </div>
                </div>
            </section>

            {/* THE PROBLEM */}
            <section className="py-28 px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>The Problem</p>
                            <h2 className="display-font text-4xl md:text-5xl font-black mb-6 leading-tight" style={{ letterSpacing: "-0.02em" }}>
                                Ambition without<br />
                                <span style={{ fontStyle: "italic" }}>evidence</span> isn't enough.
                            </h2>
                            <p className="body-font text-base leading-relaxed mb-6" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                                Admission officers and hiring managers see thousands of students with strong GPAs, AP classes, and club memberships. What stops them is the same question every time: <em>"But can this kid actually do something?"</em>
                            </p>
                            <p className="body-font text-base leading-relaxed" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                                Classroom projects end at the bell. Internships have students running coffee. Summer programs hand out certificates for sitting through lectures. None of them leave your student with something tangible to point to.
                            </p>
                        </div>
                        <div className="space-y-4">
                            {[
                                ["The experience gap", "Grades without proof of execution."],
                                ["School vs. reality", "Projects with no real users or lasting impact."],
                                ["Fear of starting", "Ideas with no structured path to make them real."],
                                ["College & career pressure", "Everyone wants initiative. Few can demonstrate it."],
                            ].map(([title, desc]) => (
                                <div key={title} className="flex gap-4 p-5 rounded-2xl" style={{ background: "#fff", border: "1px solid rgba(168,150,128,0.18)" }}>
                                    <span className="text-xl mt-0.5">⚡</span>
                                    <div>
                                        <p className="font-semibold text-sm mb-1" style={{ color: "#2a2118" }}>{title}</p>
                                        <p className="text-sm" style={{ color: "#8a7565", fontWeight: 300 }}>{desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* DIVIDER */}
            <div className="max-w-5xl mx-auto px-6">
                <div style={{ height: "1px", background: "linear-gradient(90deg, transparent, rgba(176,125,74,0.3), transparent)" }} />
            </div>

            {/* HOW IT WORKS */}
            <section id="how-it-works" className="py-28 px-6">
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-4 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>The Program</p>
                    <h2 className="display-font text-4xl md:text-5xl font-black mb-4 text-center leading-tight" style={{ letterSpacing: "-0.02em" }}>
                        Real work. Real product.<br /><span style={{ fontStyle: "italic" }}>Real stakes.</span>
                    </h2>
                    <p className="body-font text-center text-base mb-16 max-w-2xl mx-auto" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                        Your student joins a small team and spends 14 or 28 days building and launching an actual consumer product, guided every step of the way by a founder who has done it before.
                    </p>

                    {/* Week visual */}
                    <div className="grid md:grid-cols-3 gap-6 mb-16">
                        {[
                            { step: "Week 1", title: "Discover & Define", desc: "The team aligns on a real consumer problem, researches the market, and scopes what they'll build." },
                            { step: "Week 2", title: "Build & Ship", desc: "Students take ownership of product, engineering, design, and marketing roles and launch something live." },
                            { step: "Demo Day", title: "Pitch & Present", desc: "Teams present their shipped product to real founders and investors. No slides-only decks. Live demos." },
                        ].map(({ step, title, desc }) => (
                            <div key={step} className="relative p-8 rounded-3xl overflow-hidden" style={{ background: "#1e1710" }}>
                                <div className="grain absolute inset-0 z-0" />
                                <p className="relative z-10 text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>{step}</p>
                                <h3 className="relative z-10 display-font text-2xl font-bold mb-3" style={{ color: "#faf8f5" }}>{title}</h3>
                                <p className="relative z-10 text-sm leading-relaxed" style={{ color: "rgba(250,248,245,0.55)", fontWeight: 300 }}>{desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Trainer block */}
                    <div className="rounded-3xl p-10 md:p-14 flex flex-col md:flex-row gap-10 items-start" style={{ background: "#fff", border: "1px solid rgba(168,150,128,0.2)" }}>
                        <div className="flex-shrink-0 w-20 h-20 rounded-2xl flex items-center justify-center text-4xl" style={{ background: "rgba(176,125,74,0.1)" }}>
                            🏆
                        </div>
                        <div>
                            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>The Startup Trainer</p>
                            <h3 className="display-font text-3xl font-bold mb-4 leading-tight" style={{ letterSpacing: "-0.01em" }}>
                                Not a professor. A founder who has won.
                            </h3>
                            <p className="body-font text-base leading-relaxed" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                                Every Extern Academy trainer has raised over <strong style={{ color: "#2a2118" }}>$1 million in funding</strong> and scaled a real product to <strong style={{ color: "#2a2118" }}>100,000+ users</strong>. They run three structured standups per week, stay accessible on Slack throughout, and write your student's letter of recommendation at the end. This is direct access to someone who has actually done it, not someone who teaches others how it works in theory.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* OUTCOMES */}
            <section className="py-28 px-6" style={{ background: "#1e1710" }}>
                <div className="grain relative" style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
                <div className="relative max-w-5xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-4 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>What They Walk Away With</p>
                    <h2 className="display-font text-4xl md:text-5xl font-black mb-4 text-center leading-tight" style={{ color: "#faf8f5", letterSpacing: "-0.02em" }}>
                        The story that answers<br /><span style={{ fontStyle: "italic", color: "#b07d4a" }}>"Tell me about a time..."</span>
                    </h2>
                    <p className="body-font text-center text-base mb-16 max-w-xl mx-auto" style={{ color: "rgba(250,248,245,0.5)", fontWeight: 300 }}>
                        By the end of the program, your student has a concrete answer for every college interview, every application essay, every employer.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-5">
                        {outcomes.map(({ icon, title, desc }) => (
                            <div key={title} className="p-8 rounded-3xl" style={{ background: "rgba(250,248,245,0.04)", border: "1px solid rgba(250,248,245,0.08)" }}>
                                <div className="text-3xl mb-4">{icon}</div>
                                <h3 className="display-font text-xl font-bold mb-2" style={{ color: "#faf8f5" }}>{title}</h3>
                                <p className="text-sm leading-relaxed" style={{ color: "rgba(250,248,245,0.5)", fontWeight: 300 }}>{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ROI COMPARISON */}
            <section className="py-28 px-6">
                <div className="max-w-4xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-4 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>The Investment</p>
                    <h2 className="display-font text-4xl md:text-5xl font-black mb-4 text-center leading-tight" style={{ letterSpacing: "-0.02em" }}>
                        Put it in perspective.
                    </h2>
                    <p className="body-font text-center text-base mb-14 max-w-xl mx-auto" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                        Parents already invest in their student's future. Here's how Extern Academy compares to the alternatives.
                    </p>
                    <div className="space-y-4 mb-12">
                        {comparisons.map(({ label, note, ea }) => (
                            <div key={label} className="grid md:grid-cols-3 gap-4 p-6 rounded-2xl items-center" style={{ background: "#fff", border: "1px solid rgba(168,150,128,0.18)" }}>
                                <p className="font-semibold text-sm" style={{ color: "#8a7565" }}>{label}</p>
                                <p className="text-sm" style={{ color: "#8a7565", fontWeight: 300 }}>{note}</p>
                                <p className="text-sm font-semibold" style={{ color: "#b07d4a" }}>✓ {ea}</p>
                            </div>
                        ))}
                    </div>

                    {/* Pricing cards */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {[
                            { name: "2027 Startup Sprint", price: "$850", pace: "Sprint", hours: "5–10 hrs/week", outcome: "Shipped prototype + letter of rec", deadline: "Apply by Dec 31", tag: null },
                            { name: "Founder Track", price: "$2,200", pace: "Deep iteration", hours: "8–12 hrs/week", outcome: "Refined product + C-Suite title + letter of rec", deadline: "Apply by Jan 31", tag: "Most popular" },
                        ].map(({ name, price, pace, hours, outcome, deadline, tag }) => (
                            <div key={name} className="relative p-8 rounded-3xl" style={{ background: tag ? "#1e1710" : "#fff", border: tag ? "none" : "1px solid rgba(168,150,128,0.2)" }}>
                                {tag && <div className="grain absolute inset-0 rounded-3xl" />}
                                {tag && <span className="relative z-10 inline-block text-xs font-semibold tracking-widest uppercase mb-4 px-3 py-1 rounded-full" style={{ background: "rgba(176,125,74,0.2)", color: "#c9956a", letterSpacing: "0.1em" }}>{tag}</span>}
                                <p className="relative z-10 display-font text-3xl font-black mb-1" style={{ color: tag ? "#faf8f5" : "#2a2118" }}>{price}</p>
                                <p className="relative z-10 text-sm mb-6" style={{ color: tag ? "rgba(250,248,245,0.5)" : "#8a7565", fontWeight: 300 }}>{name}</p>
                                <ul className="relative z-10 space-y-3 mb-8">
                                    {[pace, hours, outcome].map(item => (
                                        <li key={item} className="flex gap-3 text-sm" style={{ color: tag ? "rgba(250,248,245,0.7)" : "#6b5d4f", fontWeight: 300 }}>
                                            <span style={{ color: "#b07d4a" }}>✓</span> {item}
                                        </li>
                                    ))}
                                </ul>
                                <a href="#apply"
                                   className="relative z-10 block text-center py-3.5 rounded-full text-sm font-semibold transition-all duration-200"
                                   style={tag
                                       ? { background: "#b07d4a", color: "#fff" }
                                       : { background: "transparent", color: "#b07d4a", border: "1.5px solid #b07d4a" }
                                   }
                                >
                                    Apply Free →
                                </a>
                                <p className="relative z-10 text-center text-xs mt-3" style={{ color: tag ? "rgba(250,248,245,0.3)" : "#b3a696", fontWeight: 300 }}>{deadline}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-28 px-6" style={{ background: "#fdf9f5" }}>
                <div className="max-w-3xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-4 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>Common Questions</p>
                    <h2 className="display-font text-4xl md:text-5xl font-black mb-14 text-center leading-tight" style={{ letterSpacing: "-0.02em" }}>
                        Things parents always ask.
                    </h2>
                    <div>
                        {faqs.map(faq => <FAQ key={faq.q} {...faq} />)}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section id="apply" className="py-36 px-6 relative overflow-hidden" style={{ background: "#1e1710" }}>
                <div className="grain absolute inset-0 z-0" />
                <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-10"
                     style={{ background: "radial-gradient(circle, #b07d4a 0%, transparent 70%)" }} />
                <div className="relative z-10 max-w-3xl mx-auto text-center">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>Ready?</p>
                    <h2 className="display-font text-5xl md:text-6xl font-black mb-6 leading-tight" style={{ color: "#faf8f5", letterSpacing: "-0.02em" }}>
                        Turn their potential<br /><span style={{ fontStyle: "italic", color: "#b07d4a" }}>into proof.</span>
                    </h2>
                    <p className="body-font text-lg mb-10 max-w-xl mx-auto" style={{ color: "rgba(250,248,245,0.55)", fontWeight: 300 }}>
                        Applications are free and take 30 minutes. Cohorts are small by design. Spots go fast.
                    </p>
                    <a
                        href="https://externacademy.com/apply"
                        className="inline-block px-10 py-5 rounded-full text-base font-bold transition-all duration-200"
                        style={{ background: "#b07d4a", color: "#fff", letterSpacing: "0.02em" }}
                        onMouseEnter={e => e.target.style.background = "#96683b"}
                        onMouseLeave={e => e.target.style.background = "#b07d4a"}
                    >
                        Apply Free. No Cost to Start →
                    </a>
                    <p className="text-xs mt-5" style={{ color: "rgba(250,248,245,0.25)", fontWeight: 300 }}>
                        June cohort deadline: April 30 &nbsp;·&nbsp; July cohort deadline: May 31
                    </p>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="py-10 px-6 text-center" style={{ background: "#160e08" }}>
                <p className="display-font text-lg font-bold mb-1" style={{ color: "#faf8f5" }}>
                    Extern <span style={{ color: "#b07d4a" }}>Academy</span>
                </p>
                <p className="text-xs" style={{ color: "rgba(250,248,245,0.25)", fontWeight: 300 }}>
                    © 2025 Extern Academy · <a href="https://externacademy.com" style={{ color: "rgba(250,248,245,0.25)" }}>externacademy.com</a>
                </p>
            </footer>
        </div>
    );
}